import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift, size, autoUpdate } from '@floating-ui/react-dom';
import { Button } from '../button/Button';
import {
  ChevronDown,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
}

export interface FilterConfig {
  key: string;
  label: string;
  type: 'single' | 'multiple';
  options: FilterOption[];
  defaultValue?: string;
  icon?: React.ComponentType<{ className?: string }>;
  getIcon?: (value: string) => React.ComponentType<{ className?: string }>;
}

export interface FiltersPanelProps<T = Record<string, any>> {
  isOpen: boolean;
  onClose: () => void;
  onFiltersChange: (filters: T) => void;
  filters: T;
  filterConfigs: FilterConfig[];
  title?: string;
  className?: string;
  isEmbedded?: boolean;
}

/**
 * One filter's option list. The list is portaled to <body> and positioned by
 * Floating UI, so a scrolling or clipping ancestor (this panel's own scroll
 * area, the Popover it usually sits in) can't cut it off, and it flips above
 * the trigger when there's no room below. `data-combobox-dropdown` is the
 * marker `Popover` skips in its outside-click check, so picking an option
 * doesn't close the popover around the panel.
 */
function FilterDropdown({
  isOpen,
  onClose,
  trigger,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
}) {
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `min(${availableHeight}px, 15rem)`,
          });
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if ((refs.reference.current as HTMLElement | null)?.contains(target) || refs.floating.current?.contains(target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, refs]);

  return (
    <div ref={refs.setReference}>
      {trigger}
      {isOpen && createPortal(
        <div
          ref={refs.setFloating}
          // Position is computed asynchronously, so the first frame would
          // paint at the viewport's top-left; hide it until it has landed.
          style={{ ...floatingStyles, visibility: isPositioned ? 'visible' : 'hidden' }}
          data-combobox-dropdown="true"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[100001] overflow-y-auto"
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

export function FiltersPanel<T extends Record<string, any>>({
  isOpen,
  onClose,
  onFiltersChange,
  filters,
  filterConfigs,
  title = "Filters",
  className = "",
  isEmbedded = false
}: FiltersPanelProps<T>) {
  const [localFilters, setLocalFilters] = useState<T>(filters);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Stable, so an open FilterDropdown doesn't re-register its document
  // listener on every render of this panel.
  const closeDropdown = useCallback(() => setOpenDropdown(null), []);

  // Update local state if parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    // Only close the dropdown for single-select filters
    const config = filterConfigs.find(c => c.key === key);
    if (config && config.type === 'single') {
      setOpenDropdown(null);
    }
  };

  const handleMultipleToggle = (key: string, value: string) => {
    const currentValues = (localFilters[key] as string[] || []);
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    // Do not close the dropdown for multi-select
    setLocalFilters(prev => ({ ...prev, [key]: newValues }));
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
    if (onClose) onClose();
  };

  const clearAllFilters = () => {
    setLocalFilters({} as T);
  };

  const hasActiveFilters = useMemo(() => {
    if (Object.keys(localFilters).length === 0) return false;
    
    return Object.keys(localFilters).some(key => {
      const value = localFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      const config = filterConfigs.find(c => c.key === key);
      if (config?.defaultValue) {
        if (value !== config.defaultValue) {
          // if there is a value and it is different from default
          return !!value;
        }
        // if value is same as default, check if other filters are active
        return Object.keys(localFilters).length > 1;
      }
      return !!value && value !== 'all';
    });
  }, [localFilters, filterConfigs]);

  const getFilterIcon = useCallback((config: FilterConfig, value?: string) => {
    // Check if this represents "all items" state
    const isAllItemsState = config.type === 'multiple' 
      ? (localFilters[config.key] as string[])?.length === 0 || !localFilters[config.key]
      : !value || value === 'all' || value === config.defaultValue;
    
    // Use filters icon for "all items" state
    if (isAllItemsState) {
      return SlidersHorizontal;
    }
    
    // Try to get icon from getIcon function first
    if (config.getIcon && value) {
      const iconComponent = config.getIcon(value);
      if (iconComponent) {
        return iconComponent;
      }
    }
    
    // Fallback to config icon
    if (config.icon) {
      return config.icon;
    }
    
    // Final fallback to default icon
    return SlidersHorizontal;
  }, [localFilters]);

  const getPluralizedLabel = useCallback((label: string) => {
    // Handle specific cases for proper pluralization
    if (label === 'Status') return 'Statuses';
    if (label === 'Category') return 'Categories';
    if (label === 'Priority') return 'Priorities';
    // For most other cases, just add 's'
    if (label.endsWith('s')) return label;
    return `${label}s`;
  }, []);

  const getFilterDisplayValue = useCallback((config: FilterConfig) => {
    const value = localFilters[config.key];
    if (config.type === 'multiple') {
      const count = (value as string[])?.length || 0;
      return count > 0 ? `${count} selected` : `All ${getPluralizedLabel(config.label)}`;
    } else {
      const selectedOption = config.options.find(o => o.value === value);
      return selectedOption?.label || `All ${getPluralizedLabel(config.label)}`;
    }
  }, [localFilters, getPluralizedLabel]);

  if (!isOpen && !isEmbedded) return null;

  const content = (
    <div className="flex flex-col h-full">
      {!isEmbedded && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
          <p className="text-sm text-gray-500">Apply filters to narrow down results.</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-4">
        {filterConfigs.map((config) => {
          // Safe icon resolution
          const IconComponent = getFilterIcon(config, config.type === 'single' ? localFilters[config.key] as string : undefined);
          
          return (
            <div key={config.key}>
              <h3 className="text-sm font-medium text-foreground mb-2">
                {config.label}
              </h3>
              <FilterDropdown
                isOpen={openDropdown === config.key}
                onClose={closeDropdown}
                trigger={
                  <Button
                    variant="outline"
                    onClick={() => setOpenDropdown(openDropdown === config.key ? null : config.key)}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      {React.createElement(IconComponent, { className: "h-4 w-4" })}
                      <span>{getFilterDisplayValue(config)}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${
                      openDropdown === config.key ? 'rotate-180' : ''
                    }`} />
                  </Button>
                }
              >
                <div className="py-1">
                  {/* All option */}
                  <button
                    onClick={() => {
                      if (config.type === 'multiple') {
                        handleFilterChange(config.key, []);
                      } else {
                        handleFilterChange(config.key, config.defaultValue || 'all');
                      }
                    }}
                    className={`flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                      (config.type === 'multiple' 
                        ? (localFilters[config.key] as string[])?.length === 0
                        : localFilters[config.key] === (config.defaultValue || 'all')
                      ) ? 'bg-primary/10 text-primary dark:bg-primary/15' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {React.createElement(SlidersHorizontal, { className: "h-4 w-4" })}
                      <span>All {getPluralizedLabel(config.label)}</span>
                    </div>
                  </button>
                  
                  {/* Individual options */}
                  {config.options.map((option) => {
                    // Safely determine the option icon
                    const OptionIcon = option.icon || IconComponent || SlidersHorizontal;
                    const isSelected = config.type === 'multiple' 
                      ? (localFilters[config.key] as string[])?.includes(option.value)
                      : localFilters[config.key] === option.value;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (config.type === 'multiple') {
                            handleMultipleToggle(config.key, option.value);
                          } else {
                            handleFilterChange(config.key, option.value);
                          }
                        }}
                        className={`flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          isSelected ? 'bg-primary/10 text-primary dark:bg-primary/15' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {config.type === 'multiple' && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}} // Handled by button click
                              className="h-3 w-3 text-primary focus:ring-primary border-gray-300 rounded pointer-events-none"
                            />
                          )}
                          {React.createElement(OptionIcon, { className: "h-4 w-4 flex-shrink-0" })}
                          <span className="truncate">{option.label}</span>
                        </div>
                        {/*option.count !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {option.count}
                          </span>
                        )*/}
                      </button>
                    );
                  })}
                </div>
              </FilterDropdown>
            </div>
          );
        })}
      </div>

      {isEmbedded && (
        <div className="pt-6 flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
          >
            Clear
          </Button>
          <Button size="sm" onClick={applyFilters}>Apply Filters</Button>
        </div>
      )}

      {!isEmbedded && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={applyFilters}>Apply Filters</Button>
        </div>
      )}
    </div>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <div 
      className={cn(
        "fixed inset-y-0 right-0 w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-50 flex flex-col transition-transform transform",
        isOpen ? "translate-x-0" : "translate-x-full",
        className
      )}
    >
      {content}
    </div>
  );
}